import { useController, useFormContext } from "react-hook-form"

import { Skeleton } from "@particles/index"

import toggleStyles from "@atoms/Toggle/Toggle.module.scss"

import { InputName } from "@molecules/Form/inputs/Input/Input.types"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"

import styles from "./ToggleListItem.module.scss"
import {
    ControlledToggleListItemProps,
    ToggleListItemProps,
    ToggleListItemRendererProps,
    UncontrolledToggleListItemProps,
} from "./ToggleListItem.types"

function ToggleIndicator(props: { checked: boolean; isLoading: boolean }) {
    const { checked, isLoading } = props
    const dataState = checked ? "checked" : "unchecked"

    return (
        <div className={toggleStyles.base} data-state={dataState}>
            <div className={toggleStyles.toggleWrapper}>
                {isLoading ? (
                    <Skeleton borderRadius={24} width={32} height={20} />
                ) : (
                    <div className={toggleStyles.toggle}>
                        <div>
                            <span className={toggleStyles.thumb} data-state={dataState} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

function ToggleListItemRenderer(props: ToggleListItemRendererProps) {
    const { label, caption, checked, onClick, isLoading = false, isDisabled = false } = props

    return (
        <ListItem isClickable={true} onClick={onClick} isDisabled={isDisabled} isLoading={isLoading}>
            <ListItem.AvatarAndContent
                content={
                    <ListItemContent
                        leftLabel={<ListItem.Label label={label} />}
                        leftCaption={caption ? <ListItem.Caption caption={caption} /> : null}
                    />
                }
            />
            <ListItem.Actions>
                <div className={styles.toggleWrapper} aria-hidden="true">
                    <ToggleIndicator checked={checked} isLoading={isLoading} />
                </div>
            </ListItem.Actions>
        </ListItem>
    )
}

function ControlledToggleListItem<T extends InputName = InputName>(props: ControlledToggleListItemProps<T>) {
    const { label, caption, isLoading = false, isDisabled = false, name } = props
    const form = useFormContext()
    const { field } = useController({ name: name as string, control: form.control })

    const handleClick = () => {
        field.onChange(!field.value)
    }

    return (
        <ToggleListItemRenderer
            label={label}
            caption={caption}
            checked={(field.value as boolean) ?? false}
            onClick={handleClick}
            isLoading={isLoading}
            isDisabled={isDisabled}
        />
    )
}

function UncontrolledToggleListItem(props: UncontrolledToggleListItemProps) {
    const { label, caption, isLoading = false, isDisabled = false, checked = false, onCheckedChange } = props

    const handleClick = () => {
        onCheckedChange?.(!checked)
    }

    return (
        <ToggleListItemRenderer
            label={label}
            caption={caption}
            checked={checked}
            onClick={handleClick}
            isLoading={isLoading}
            isDisabled={isDisabled}
        />
    )
}

function ToggleListItem<T extends InputName = InputName>(props: ToggleListItemProps<T>) {
    if (props.isControlled) {
        return <ControlledToggleListItem<T> {...props} />
    }

    return <UncontrolledToggleListItem {...props} />
}

export default ToggleListItem
